import { useState, useEffect } from "react";
import { Play, Pause, RotateCcw, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AudioPlayerProps {
  src: string;
  audioRef: React.RefObject<HTMLAudioElement>;
}

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function AudioPlayer({ src, audioRef }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("durationchange", handleDurationChange);
    audio.addEventListener("loadedmetadata", handleDurationChange);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("loadedmetadata", handleDurationChange);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioRef]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 10);
  };

  const handleSpeedChange = (speed: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.playbackRate = speed;
    setPlaybackSpeed(speed);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audio.currentTime = percent * duration;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="overflow-hidden md:rounded-2xl">
      <div className="w-full rounded-2xl border border-border bg-card p-2 pr-3">
        <div className="flex items-center gap-2">
          {/* Skip Backward */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
            onClick={skipBackward}
            aria-label="Skip backward 10 seconds"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>

          {/* Play/Pause */}
          <button
            type="button"
            className="rounded-lg bg-primary/5 p-2 transition-colors hover:bg-primary/10 focus:outline-none dark:bg-primary/10 dark:hover:bg-primary/20"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-5 w-5 text-primary/60 hover:text-primary/80 dark:text-primary/80 dark:hover:text-primary" />
            ) : (
              <Play className="h-5 w-5 text-primary/60 hover:text-primary/80 dark:text-primary/80 dark:hover:text-primary" />
            )}
          </button>

          {/* Skip Forward */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground"
            onClick={skipForward}
            aria-label="Skip forward 10 seconds"
          >
            <RotateCw className="h-4 w-4" />
          </Button>

          {/* Progress Bar */}
          <div
            className="h-8 flex-grow cursor-pointer rounded bg-muted/50"
            onClick={handleProgressClick}
          >
            <div className="flex h-full items-center px-1">
              <div className="relative h-1 w-full rounded-full bg-muted">
                <div
                  className="absolute left-0 top-0 h-full rounded-full bg-primary/50"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Playback Speed */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-fit rounded-full bg-primary/5 px-2 py-1.5 text-center text-xs text-primary/70 transition-colors hover:bg-primary/10 hover:text-primary/80 focus:outline-none dark:bg-primary/10 dark:text-primary/80 dark:hover:bg-primary/20 dark:hover:text-primary"
                aria-label="Playback speed"
              >
                {playbackSpeed}x
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PLAYBACK_SPEEDS.map((speed) => (
                <DropdownMenuItem
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  className={playbackSpeed === speed ? "bg-accent" : ""}
                >
                  {speed}x
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Time Display */}
          <div className="w-10 whitespace-nowrap text-center text-sm text-primary/70">
            {formatTime(currentTime)}
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src={src} hidden preload="metadata" />
      </div>
    </div>
  );
}
